import { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import { PENDING_TABLE_PER_PAGE, PENDING_TAB_BADGE } from './common/pendingTableConfig';
import { buildPendingActivityAtColumn, PENDING_ACTIVITY_AT_SORT } from './common/pendingActivityAtColumn';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import PendingListStatusBadge from './common/PendingListStatusBadge';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight, PendingTotalPriceHighlight, PendingAmountHighlight } from './common/PendingKeywordHighlight';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import PendingListStatsPanel from './common/PendingListStatsPanel';
import PendingListStatsOverlay from './common/PendingListStatsOverlay';
import { getPendingListStatusBadgeProps } from './common/pendingRowStatusDisplay';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import AccountsReceivableModal from './modal/AccountsReceivableModal';

const AccountsReceivableOrderPendings = () => {
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const { arOrders = [], isLoadingAROrders, setCurrentOrderOIdState } = useOrderContext();
	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(arOrders);
	const totalAccountsReceivable = useMemo(
		() => arOrders.reduce((sum, o) => sum + (Number(o.currentAccountsReceivable) || 0), 0),
		[arOrders],
	);

	const statsItems = useMemo(() => {
		if (!(Number(totalAccountsReceivable) > 0)) return [];
		return [
			{
				key: 'accountsReceivable',
				label: '总应收账款',
				amount: Number(totalAccountsReceivable),
				count: Array.isArray(arOrders) ? arOrders.length : 0,
				active: true,
			},
		];
	}, [arOrders, totalAccountsReceivable]);

	const handleOrderClick = useCallback(
		(orderId) => {
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(orderId);
			socket.emit('join order', orderId);
		},
		[setCurrentOrderOIdState],
	);

	const formatDateTime = useCallback((date) => {
		return moment(date).format('YYYY-MM-DD HH:mm');
	}, []);

	const columns = useMemo(
		() => [
			{
				accessorKey: 'orderId',
				header: '订单编号',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: {
						className: 'fw-semibold text-start',
					},
				},
				cell: ({ row: { original } }) => {
					const { orderId, _id } = original;
					return (
						<PendingOrderIdCell
							orderId={orderId}
							orderMongoId={_id}
							badgeVariant={PENDING_TAB_BADGE.accountsReceivable}
							onOrderClick={handleOrderClick}
							order={original}
						/>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const { customerInfo } = original;
					return (
						<span className={`link-${PENDING_TAB_BADGE.accountsReceivable}`}>
							<PendingKeywordHighlight text={customerInfo?.customerName} />
						</span>
					);
				},
			},
			{
				accessorKey: 'currentAccountsReceivable',
				header: '应收账款',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-end' },
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					const { currentAccountsReceivable } = original;
					return (
						<PendingListStatusBadge
							bg={PENDING_TAB_BADGE.accountsReceivable}
							text={<PendingAmountHighlight amount={currentAccountsReceivable} />}
							pill
							showIcon={false}
						/>
					);
				},
			},
			{
				accessorKey: 'paymentStatus',
				header: '待收尾款',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => {
					const config = getPendingListStatusBadgeProps(
						original.paymentStatus,
						PENDING_TAB_BADGE.accountsReceivable,
						'payment',
					);
					if (!config) {
						return <span>-</span>;
					}
					return <PendingListStatusBadge bg={config.bg} text={config.text} />;
				},
			},
			{
				accessorKey: 'totalPrice',
				header: '总价',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <PendingTotalPriceHighlight totalPrice={original?.totalPrice} className='fs-11' />;
				},
			},
			buildPendingActivityAtColumn(formatDateTime),
			{
				accessorKey: 'createdAt',
				header: '创建时间',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <span className='fs-11'>{original?.createdAt ? formatDateTime(original.createdAt) : '-'}</span>;
				},
			},
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					cellProps: {
						className: 'text-end py-2',
					},
				},
				cell: ({ row: { original } }) => {
					const detailUrl = original?._id ? `/order/detail/${original._id}` : '#';
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item href={detailUrl}>查看</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		],
		[handleOrderClick, formatDateTime],
	);

	const table = useAdvanceTable({
		data: filteredOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			sorting: PENDING_ACTIVITY_AT_SORT,
		},
	});

	if (isLoadingAROrders) {
		return <PendingListLoadingState />;
	}

	const showFooter = arOrders.length > 0 || hasKeyword;

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar
					statsMobileOnly
					stats={<PendingListStatsPanel items={statsItems} showChart={false} />}
					statsOverlay={<PendingListStatsOverlay items={statsItems} />}
				>
					{filteredOrders.length === 0 ? (
						<PendingListEmptyState
							emptyTitle={hasKeyword ? '无匹配订单' : '暂无应收账款'}
							emptyDescription={hasKeyword ? `当前待办中没有包含「${keyword}」的订单。` : '目前没有需要你处理的应收账款。'}
						/>
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={filteredOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<AccountsReceivableModal setIsOpenPendingModal={setIsOpenPendingModal} isOpenPendingModal={isOpenPendingModal} />
		</>
	);
};

export default AccountsReceivableOrderPendings;
